import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Stack,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  CloudUpload as DeployIcon,
} from '@mui/icons-material';
import api from '../api';
import { useAuth } from '../AuthContext';

function ServerDeployManager() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [confirmServer, setConfirmServer] = useState(null);
  const [lastOutput, setLastOutput] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const showAlert = (message, severity = 'info') => {
    const alert = { id: Date.now(), message, severity };
    setAlerts((prev) => [...prev, alert]);
    setTimeout(() => {
      setAlerts((prev) => prev.filter((a) => a.id !== alert.id));
    }, 8000);
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['serverDeployTargets'],
    queryFn: async () => {
      const response = await api.get('/server-deploy/servers');
      return response.data;
    },
    enabled: user?.role === 'super_admin',
  });

  const runMutation = useMutation({
    mutationFn: async (serverId) => {
      const response = await api.post(
        `/server-deploy/run/${serverId}`,
        {},
        { timeout: 21 * 60 * 1000 }
      );
      return response.data;
    },
    onSuccess: (result) => {
      setLastOutput(result);
      setConfirmServer(null);
      queryClient.invalidateQueries(['serverDeployTargets']);
      if (result.success) {
        showAlert(`${result.server.name} updated successfully (${Math.round(result.durationMs / 1000)}s)`, 'success');
      } else {
        showAlert(`${result.server.name} update failed (exit ${result.exitCode})`, 'error');
      }
    },
    onError: (error) => {
      setConfirmServer(null);
      showAlert(error.response?.data?.error || error.message || 'Deploy failed', 'error');
    },
  });

  if (user?.role !== 'super_admin') {
    return (
      <Box p={3}>
        <Alert severity="error">Server deploy requires super admin privileges.</Alert>
      </Box>
    );
  }

  const handleRun = () => {
    if (confirmServer) {
      runMutation.mutate(confirmServer.id);
    }
  };

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <DeployIcon color="primary" sx={{ fontSize: 32 }} />
        <Box flex={1}>
          <Typography variant="h5">Server Deploy</Typography>
          <Typography variant="body2" color="text.secondary">
            Run <code>update.sh</code> on production hosts (local) or dev game API (SSH from PTAdmin).
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={() => refetch()} disabled={isLoading}>
          Refresh
        </Button>
      </Stack>

      {alerts.map((alert) => (
        <Alert key={alert.id} severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      ))}

      {data && !data.sshKeyConfigured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          SSH key not found on PTAdmin host — dev game API deploy will fail. Set{' '}
          <code>DEPLOY_SSH_KEY_PATH</code> in <code>.env</code>.
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Server</TableCell>
                <TableCell>Path</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : (
                data?.servers?.map((server) => (
                  <TableRow key={server.id}>
                    <TableCell>
                      <Typography fontWeight={600}>{server.name}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {server.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {server.repoPath}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={server.mode === 'ssh' ? `SSH → ${server.host}` : 'Local'}
                        color={server.mode === 'ssh' ? 'info' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={
                          runMutation.isPending && confirmServer?.id === server.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <PlayIcon />
                          )
                        }
                        disabled={runMutation.isPending}
                        onClick={() => setConfirmServer(server)}
                      >
                        Run update.sh
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {lastOutput && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <Typography variant="h6">Last deploy output</Typography>
            <Chip
              size="small"
              label={lastOutput.success ? 'Success' : 'Failed'}
              color={lastOutput.success ? 'success' : 'error'}
            />
            <Typography variant="caption" color="text.secondary">
              {lastOutput.server?.name} · exit {lastOutput.exitCode} ·{' '}
              {Math.round((lastOutput.durationMs || 0) / 1000)}s
            </Typography>
          </Stack>
          <TextField
            fullWidth
            multiline
            minRows={12}
            maxRows={24}
            value={lastOutput.output || '(no output)'}
            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
          />
        </Paper>
      )}

      <Dialog open={Boolean(confirmServer)} onClose={() => !runMutation.isPending && setConfirmServer(null)}>
        <DialogTitle>Run update.sh?</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            This will run <strong>{confirmServer?.name}</strong> deploy script:
          </Typography>
          <Typography variant="body2" fontFamily="monospace" sx={{ mb: 2 }}>
            cd {confirmServer?.repoPath} && ./update.sh
          </Typography>
          <Alert severity="warning">
            May take up to {data?.timeoutMinutes || 20} minutes. Do not close this tab while running.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmServer(null)} disabled={runMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRun}
            disabled={runMutation.isPending}
            startIcon={runMutation.isPending ? <CircularProgress size={16} /> : <PlayIcon />}
          >
            {runMutation.isPending ? 'Running…' : 'Run update.sh'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ServerDeployManager;
